import React, { useEffect, useMemo, useRef, useState } from "react";
import { useChatbot } from "../../hooks/useChatbot";
import ChatMessageBubble from "./ChatMessageBubble";
import ChatInput from "./ChatInput";
import "./ChatWidget.css";

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  content:
    "Hi! 👋 I'm the SVA Assistant. I can help students, teachers, and anyone with questions about SVA. What can I help you with today?",
  timestamp: new Date(),
};

const SUGGESTED_QUESTIONS = [
  "How do I log my volunteer hours?",
  "Where are my badges?",
  "How do I create an SVA account?",
  "The app is not working — can you help?",
];

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const messagesEndRef = useRef(null);

  const { messages, isLoading, error, sendMessage, clearSession } = useChatbot();

  const allMessages = useMemo(
    () => (messages.length === 0 ? [WELCOME_MESSAGE] : [WELCOME_MESSAGE, ...messages]),
    [messages]
  );

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, allMessages]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && !isOpen) {
      setHasUnread(true);
    }
  }, [messages, isOpen]);

  const handleSend = async (text, image) => {
    await sendMessage(text, image);
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  return (
    <>
      {isOpen && (
        <div className="chat-widget-panel" role="dialog" aria-label="SVA Support Chat">
          <div className="chat-widget-header">
            <div className="chat-widget-header-info">
              <div className="chat-widget-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>
              <div>
                <div className="chat-widget-title">SVA Assistant</div>
                <div className="chat-widget-subtitle">Here to help students & teachers</div>
              </div>
            </div>
            <div className="chat-widget-header-actions">
              <button
                className="chat-widget-icon-btn"
                onClick={clearSession}
                title="Start new conversation"
                aria-label="Start new conversation"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                </svg>
              </button>
              <button
                className="chat-widget-icon-btn"
                onClick={() => setIsOpen(false)}
                title="Close chat"
                aria-label="Close chat"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="chat-widget-messages" role="log" aria-live="polite">
            {allMessages.map((msg) => (
              <ChatMessageBubble key={msg.id} message={msg} />
            ))}

            {messages.length === 0 && (
              <div className="chat-widget-suggestions">
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    className="chat-widget-suggestion-chip"
                    onClick={() => handleSuggestedQuestion(q)}
                    type="button"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {isLoading && (
              <div className="chat-widget-typing">
                <span />
                <span />
                <span />
              </div>
            )}

            {error && (
              <div className="chat-widget-error" role="alert">
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <ChatInput onSend={handleSend} disabled={isLoading} />
        </div>
      )}

      <button
        className={`chat-widget-bubble ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close chat" : "Open chat"}
        type="button"
      >
        {hasUnread && !isOpen && <span className="chat-widget-unread-dot" />}
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
        )}
      </button>
    </>
  );
}

